import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Button, Form, Space, Row, Col, Breadcrumb, Modal,
    message, Typography, Select, DatePicker, Input, Tag, Descriptions, Dropdown
} from "antd";
import {
    PlusOutlined, HomeOutlined, AccountBookOutlined, FilterOutlined,
    ClearOutlined, SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined, MoreOutlined, StopOutlined
} from "@ant-design/icons";
import api from "../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import "../../styles/fluidDesign.css";
import { EXPENSE_STATUSES, EXPENSE_STATUS_COLORS, APPROVAL_STATUS_COLORS } from "../../constant/expenseOptions";

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;

const expStatusColors = EXPENSE_STATUS_COLORS;

const ExpenseList = () => {
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [viewVisible, setViewVisible] = useState(false);
    const [viewExpense, setViewExpense] = useState(null);
    const [editingExpense, setEditingExpense] = useState(null);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    // Advanced filter state
    const [search, setSearch] = useState("");
    const [filterCat, setFilterCat] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState(undefined);
    const [filterSupplier, setFilterSupplier] = useState(undefined);
    const [filterDates, setFilterDates] = useState([]);
    const [applied, setApplied] = useState({});

    const fetchRefData = useCallback(async () => {
        try {
            const [catRes, supRes] = await Promise.all([
                api.get("/masterdropdown/listof_activeexpensecategories.php"),
                api.get("/masterdropdown/listof_activesuppliers.php"),
            ]);
            setCategories(catRes.data.dataset || []);
            setSuppliers(supRes.data.dataset || []);
        } catch {
            message.error("Failed to load reference data.");
        }
    }, []);

    const fetchExpenses = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        setLoading(true);
        try {
            const res = await api.post("/expenses/list.php", {
                page,
                limit: pageSize,
                search: filters.search || "",
                expcat_id: filters.expcat_id || 0,
                sup_id: filters.sup_id || 0,
                status: filters.status || "",
                start_date: filters.start_date || "",
                end_date: filters.end_date || "",
            });
            setExpenses(res.data.dataset || []);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch expenses.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRefData();
        fetchExpenses(1, 10, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            expcat_id: filterCat,
            sup_id: filterSupplier,
            status: filterStatus,
            start_date: filterDates?.[0] ? filterDates[0].format("YYYY-MM-DD") : "",
            end_date: filterDates?.[1] ? filterDates[1].format("YYYY-MM-DD") : "",
        };
        setApplied(filters);
        fetchExpenses(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch("");
        setFilterCat(undefined);
        setFilterStatus(undefined);
        setFilterSupplier(undefined);
        setFilterDates([]);
        setApplied({});
        fetchExpenses(1, pagination.pageSize, {});
    };

    const openView = async (record) => {
        setViewExpense(record);
        setViewVisible(true);
        try {
            // detail.php uses responseSuccess($row) which MERGES the row into the
            // top level (no `data` wrapper): { status, message, exp_id, ... }.
            const res = await api.get(`/expenses/detail.php?id=${record.exp_id}`);
            if (res.data.status === "success") setViewExpense(res.data);
        } catch {
            message.error("Failed to load expense details.");
        }
    };

    const openEdit = (record) => {
        setEditingExpense(record);
        editForm.setFieldsValue({
            expcatId: Number(record.expcat_id),
            supId: Number(record.sup_id),
            expTitle: record.exp_title,
            supplierRef: record.supplier_ref || "",
            expDesc: record.exp_desc || "",
            expAmount: record.exp_amount,
            paymentReqDate: record.payment_req_date ? dayjs(record.payment_req_date) : undefined,
        });
        setIsEditModalVisible(true);
    };

    const handleEdit = async (values) => {
        setLoading(true);
        try {
            const res = await api.post("/expenses/edit.php", {
                exp_id: editingExpense.exp_id,
                expcat_id: values.expcatId,
                sup_id: values.supId,
                exp_title: values.expTitle,
                exp_desc: values.expDesc,
                exp_amount: values.expAmount,
                supplier_ref: values.supplierRef || "",
                payment_req_date: values.paymentReqDate.format("YYYY-MM-DD"),
            });
            if (res.data.status === "success") {
                message.success(res.data.message);
                setIsEditModalVisible(false);
                editForm.resetFields();
                handleFilter();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update expense.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = (record) => {
        Modal.confirm({
            title: "Cancel Expense",
            content: `Are you sure you want to cancel expense ${record.exp_ref}? This cannot be undone.`,
            okText: "Yes, Cancel",
            okType: "danger",
            cancelText: "No",
            maskClosable: false,
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.post("/expenses/cancel.php", { exp_id: record.exp_id });
                    if (res.data.status === "success") {
                        message.success(res.data.message);
                        handleFilter();
                    } else throw new Error(res.data.message);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to cancel expense.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: "Delete Expense",
            content: `Are you sure you want to delete expense ${record.exp_ref}? This action cannot be undone.`,
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            maskClosable: false,
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.post("/expenses/delete.php", { exp_id: record.exp_id });
                    if (res.data.status === "success") {
                        message.success(res.data.message);
                        handleFilter();
                    } else throw new Error(res.data.message);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to delete expense.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleAdd = async (values) => {
        setLoading(true);
        try {
            const res = await api.post("/expenses/create.php", {
                expcat_id: values.expcatId,
                sup_id: values.supId,
                exp_title: values.expTitle,
                exp_desc: values.expDesc,
                exp_amount: values.expAmount,
                supplier_ref: values.supplierRef || "",
                payment_req_date: values.paymentReqDate.format("YYYY-MM-DD"),
            });
            if (res.data.status === "success") {
                message.success(`${res.data.message} (${res.data.data?.exp_ref})`);
                setIsModalVisible(false);
                form.resetFields();
                handleFilter();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to create expense.");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "Expense Ref", dataIndex: "exp_ref", key: "exp_ref",
            render: (v) => <Typography.Text strong>{v}</Typography.Text>,
        },
        {
            title: "Category", dataIndex: "expcat_name", key: "expcat_name",
            render: (v) => v || "-",
        },
        {
            title: "Supplier", dataIndex: "supplier_name", key: "supplier_name",
            render: (v) => v || "-",
        },
        {
            title: "Title", dataIndex: "exp_title", key: "exp_title",
            ellipsis: true,
        },
        {
            title: "Amount", dataIndex: "exp_amount", key: "exp_amount", align: "right",
            render: (v) => <strong>LKR {parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>,
        },
        {
            title: "Payment Req. Date", dataIndex: "payment_req_date", key: "payment_req_date", align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "Status", dataIndex: "exp_status", key: "exp_status", align: "center",
            render: (s) => <Tag color={expStatusColors[s] || "default"} style={{ fontWeight: 600 }}>{s}</Tag>,
        },
        {
            title: "Created By", dataIndex: "creator_name", key: "creator_name",
            render: (v) => v || "-",
        },
        {
            title: "Action", key: "action", align: "center", fixed: "right", width: 90,
            render: (_, record) => {
                const editable = record.approved_status === "Pending" || record.approved_status === "Rejected";
                const cancellable = record.approved_status === "Pending";
                const deletable = editable || record.approved_status === "Cancel";
                // NOTE: menu item onClick receives the antd MenuInfo object whose
                // domEvent must be stopped from propagating — otherwise the same
                // click that selects the item lands on the just-opened modal mask
                // (clickable by default) and instantly dismisses the modal.
                const items = [
                    {
                        key: "view", label: "View Details", icon: <EyeOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openView(record); },
                    },
                    {
                        key: "edit", label: "Edit", icon: <EditOutlined />, disabled: !editable,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openEdit(record); },
                    },
                    {
                        key: "cancel", label: "Cancel Expense", icon: <StopOutlined />, danger: true, disabled: !cancellable,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleCancel(record); },
                    },
                    {
                        key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, disabled: !deletable,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleDelete(record); },
                    },
                ];
                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" className="fluid-dots-btn" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Expenses' },
                                { title: 'Expenses List' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <AccountBookOutlined /> Expenses List
                        </h1>
                        <p className="fluid-page-subtitle">Record expenses and track them through approval and payment</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsModalVisible(true)}
                                className="hero-action-btn"
                            >
                                Add Expense
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    {/* Advanced filter bar - first element inside the card,
                        directly above the records (matches the previous system) */}
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Ref, title, supplier ref"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Category:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterCat}
                                    onChange={setFilterCat}
                                    placeholder="All Categories"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {categories.map((c) => <Option key={c.value} value={Number(c.value)}>{c.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    placeholder="All Statuses"
                                    allowClear
                                >
                                    {EXPENSE_STATUSES.map((s) => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Supplier:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterSupplier}
                                    onChange={setFilterSupplier}
                                    placeholder="All Suppliers"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {suppliers.map((s) => <Option key={s.value} value={Number(s.value)}>{s.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Req. Date Range:</div>
                                <RangePicker value={filterDates} onChange={setFilterDates} style={{ width: "100%" }} />
                            </Col>
                            <Col xs={24} sm={24} lg={6}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={expenses}
                            rowKey="exp_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pg) => fetchExpenses(pg.current, pg.pageSize, applied)}
                            loading={loading}
                            scroll={{ x: 1100 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <Modal
                title="Add Expense"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
                footer={null}
                width={720}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleAdd}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Expense Category" name="expcatId" rules={[{ required: true, message: "Category is required" }]}>
                                <Select allowClear placeholder="Select Category" showSearch optionFilterProp="children">
                                    {categories.map((c) => <Option key={c.value} value={Number(c.value)}>{c.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Supplier" name="supId" rules={[{ required: true, message: "Supplier is required" }]}>
                                <Select allowClear placeholder="Select Supplier" showSearch optionFilterProp="children">
                                    {suppliers.map((s) => <Option key={s.value} value={Number(s.value)}>{s.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Expense Title" name="expTitle" rules={[{ required: true, message: "Title is required" }, { max: 200 }]}>
                                <Input placeholder="e.g. Monthly electricity bill" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Supplier Reference" name="supplierRef">
                                <Input placeholder="Optional - e.g. invoice no." maxLength={100} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Description" name="expDesc">
                        <Input.TextArea rows={2} placeholder="Optional details" maxLength={500} />
                    </Form.Item>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Amount (LKR)" name="expAmount" rules={[{ required: true, message: "Amount is required" }]}>
                                <Input type="number" step="0.01" min="0" placeholder="15000.00" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Payment Request Date" name="paymentReqDate" rules={[{ required: true, message: "Date is required" }]}>
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsModalVisible(false); form.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Create Expense</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title={viewExpense ? `Expense Details - ${viewExpense.exp_ref}` : "Expense Details"}
                open={viewVisible}
                onCancel={() => setViewVisible(false)}
                footer={<Button type="primary" onClick={() => setViewVisible(false)}>Close</Button>}
                width={720}
                maskClosable={false}
                destroyOnClose
            >
                {viewExpense && (
                    <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="Expense Ref" span={2}>
                            <Typography.Text strong>{viewExpense.exp_ref}</Typography.Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Category">{viewExpense.expcat_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Supplier">{viewExpense.supplier_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Expense Title" span={2}>{viewExpense.exp_title}</Descriptions.Item>
                        <Descriptions.Item label="Description" span={2}>{viewExpense.exp_desc || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Amount">
                            <strong>LKR {parseFloat(viewExpense.exp_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Payment Req. Date">
                            {viewExpense.payment_req_date ? dayjs(viewExpense.payment_req_date).format("YYYY-MM-DD") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Supplier Ref">{viewExpense.supplier_ref || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Expense Status">
                            <Tag color={expStatusColors[viewExpense.exp_status] || "default"} style={{ fontWeight: 600 }}>{viewExpense.exp_status}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Approval Status">
                            <Tag color={APPROVAL_STATUS_COLORS[viewExpense.approved_status] || "default"} style={{ fontWeight: 600 }}>{viewExpense.approved_status}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Created By">{viewExpense.creator_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Created Date">{viewExpense.cr_dt ? dayjs(viewExpense.cr_dt).format("YYYY-MM-DD HH:mm") : "-"}</Descriptions.Item>
                        <Descriptions.Item label="Last Updated By">{viewExpense.checker_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Last Updated Date">{viewExpense.ch_dt ? dayjs(viewExpense.ch_dt).format("YYYY-MM-DD HH:mm") : "-"}</Descriptions.Item>
                        <Descriptions.Item label="Approved By">{viewExpense.approver_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Approved Date">
                            {viewExpense.approved_dt && Number(viewExpense.approved_dt) > 0 ? dayjs.unix(Number(viewExpense.approved_dt)).format("YYYY-MM-DD HH:mm") : "-"}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            <Modal
                title="Edit Expense"
                open={isEditModalVisible}
                onCancel={() => { setIsEditModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={720}
                maskClosable={false}
                destroyOnClose
            >
                <Form form={editForm} layout="vertical" onFinish={handleEdit}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Expense Category" name="expcatId" rules={[{ required: true, message: "Category is required" }]}>
                                <Select allowClear placeholder="Select Category" showSearch optionFilterProp="children">
                                    {categories.map((c) => <Option key={c.value} value={Number(c.value)}>{c.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Supplier" name="supId" rules={[{ required: true, message: "Supplier is required" }]}>
                                <Select allowClear placeholder="Select Supplier" showSearch optionFilterProp="children">
                                    {suppliers.map((s) => <Option key={s.value} value={Number(s.value)}>{s.label}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Expense Title" name="expTitle" rules={[{ required: true, message: "Title is required" }, { max: 200 }]}>
                                <Input placeholder="e.g. Monthly electricity bill" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Supplier Reference" name="supplierRef">
                                <Input placeholder="Optional - e.g. invoice no." maxLength={100} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Description" name="expDesc">
                        <Input.TextArea rows={2} placeholder="Optional details" maxLength={500} />
                    </Form.Item>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Amount (LKR)" name="expAmount" rules={[{ required: true, message: "Amount is required" }]}>
                                <Input type="number" step="0.01" min="0" placeholder="15000.00" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Payment Request Date" name="paymentReqDate" rules={[{ required: true, message: "Date is required" }]}>
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsEditModalVisible(false); editForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<EditOutlined />} loading={loading}>Update Expense</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ExpenseList;
